import React, { useEffect } from 'react';
import PublicChrome from './public/PublicChrome';
import BlurFade from './public/motion/BlurFade';

type LegalKind = 'terms' | 'privacy';

const COPY: Record<
  LegalKind,
  { title: string; lead: string; sections: { heading: string; body: React.ReactNode }[] }
> = {
  terms: {
    title: 'Terms of Use',
    lead: 'Welcome to DUSKPROOF. By accessing or using our website and services, you agree to these Terms of Use. Please read them carefully.',
    sections: [
      {
        heading: '1. Introduction',
        body: 'These Terms of Use govern your access to and use of services provided by DUSKPROOF (“Services”), including validator operations, staking information, infrastructure tooling, and related blockchain products described on duskproof.com and affiliated surfaces.',
      },
      {
        heading: '2. Definitions',
        body: (
          <ul className="list-disc pl-5 m-0 space-y-2">
            <li>
              <strong className="text-[var(--dusk-ink)]">“DUSKPROOF”, “we”, “us”, “our”</strong> — DUSKPROOF and its operators
            </li>
            <li>
              <strong className="text-[var(--dusk-ink)]">“Services”</strong> — validator services, monitoring products, dashboards, APIs, and public site content
            </li>
            <li>
              <strong className="text-[var(--dusk-ink)]">“User”</strong> — any individual or entity using the Services
            </li>
            <li>
              <strong className="text-[var(--dusk-ink)]">“Platform”</strong> — DUSKPROOF websites, apps, APIs, and tools
            </li>
            <li>
              <strong className="text-[var(--dusk-ink)]">“Staking”</strong> — delegating or locking tokens to support a blockchain network
            </li>
            <li>
              <strong className="text-[var(--dusk-ink)]">“Slashing”</strong> — a network-level penalty that may affect staked funds
            </li>
            <li>
              <strong className="text-[var(--dusk-ink)]">“Delegators”</strong> — users who delegate assets to validators operated by DUSKPROOF
            </li>
          </ul>
        ),
      },
      {
        heading: '3. Services provided',
        body: (
          <>
            <p className="m-0 mb-3">DUSKPROOF may provide blockchain infrastructure and related services, including:</p>
            <ul className="list-disc pl-5 m-0 space-y-1.5">
              <li>Validator operations and monitoring</li>
              <li>Node and infrastructure maintenance</li>
              <li>Analytics, uptime, and network intelligence products</li>
              <li>Public endpoints and tooling where offered</li>
              <li>Educational and reference materials on this website</li>
            </ul>
          </>
        ),
      },
      {
        heading: '4. Intellectual property',
        body: 'Content, software, trademarks, and materials on the Platform are owned by DUSKPROOF or its licensors. Unauthorized use, reproduction, or distribution is prohibited. Brand assets are available via the Brand Kit for approved uses only.',
      },
      {
        heading: '5. User obligations',
        body: (
          <ul className="list-disc pl-5 m-0 space-y-1.5">
            <li>Use the Services in compliance with applicable laws</li>
            <li>Do not exploit, disrupt, or interfere with the Platform</li>
            <li>Provide accurate information when requested</li>
          </ul>
        ),
      },
      {
        heading: '6. User responsibilities',
        body: (
          <ul className="list-disc pl-5 m-0 space-y-1.5">
            <li>Taxes and legal obligations related to staking or rewards</li>
            <li>Monitoring delegated assets and network conditions</li>
            <li>Understanding risks of blockchain participation</li>
          </ul>
        ),
      },
      {
        heading: '7. Risks and limitations',
        body: (
          <ul className="list-disc pl-5 m-0 space-y-1.5">
            <li>
              <strong className="text-[var(--dusk-ink)]">Market risks</strong> — token values may fluctuate significantly
            </li>
            <li>
              <strong className="text-[var(--dusk-ink)]">Technical risks</strong> — networks may fail, fork, or delay
            </li>
            <li>
              <strong className="text-[var(--dusk-ink)]">Regulatory risks</strong> — laws may change and affect availability
            </li>
          </ul>
        ),
      },
      {
        heading: '8. Slashing & compensation',
        body: 'DUSKPROOF may consider compensation for slashing only where the loss is directly caused by proven operational negligence on our side. Any remedy is evaluated case by case and is not guaranteed.',
      },
      {
        heading: '9. Governing law',
        body: 'These Terms are governed by the laws of the jurisdiction where DUSKPROOF operates. Disputes are handled under those laws.',
      },
      {
        heading: '10. Disclaimers',
        body: 'Services are provided “as is” and “as available”. DUSKPROOF makes no warranties and is not liable for indirect, incidental, or consequential losses, including staking rewards shortfalls or market losses.',
      },
      {
        heading: '11. Amendments',
        body: 'We may update these Terms at any time. Continued use of the Services after changes means you accept the updated Terms.',
      },
      {
        heading: '12. Contact',
        body: (
          <>
            Questions or partnerships:{' '}
            <a href="mailto:office@duskproof.com" className="text-[var(--dusk-copper)] no-underline hover:text-[var(--dusk-amber)]">
              office@duskproof.com
            </a>
            {' · '}
            <a href="mailto:partners@duskproof.com" className="text-[var(--dusk-copper)] no-underline hover:text-[var(--dusk-amber)]">
              partners@duskproof.com
            </a>
          </>
        ),
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    lead: 'This Privacy Policy describes how DUSKPROOF (“we”, “us”, or “our”) may collect, process, or share personal data when you use our website and Services. We do not sell personal data. Material changes will be reflected by updating the “last updated” date below.',
    sections: [
      {
        heading: '1. Data collection',
        body: (
          <>
            <p className="m-0 mb-3">While providing Services, we may collect:</p>
            <ul className="list-disc pl-5 m-0 space-y-1.5">
              <li>
                <strong className="text-[var(--dusk-ink)]">Personal data</strong> — name, email, social handles you share with us
              </li>
              <li>
                <strong className="text-[var(--dusk-ink)]">Technical information</strong> — IP address, OS, browser type
              </li>
              <li>
                <strong className="text-[var(--dusk-ink)]">Usage data</strong> — how you interact with the site or products
              </li>
              <li>
                <strong className="text-[var(--dusk-ink)]">Blockchain data</strong> — on-chain activity is public and may be visible without revealing off-chain identity
              </li>
            </ul>
          </>
        ),
      },
      {
        heading: '2. Data use',
        body: (
          <ul className="list-disc pl-5 m-0 space-y-1.5">
            <li>Providing and improving Services</li>
            <li>Service-related communications and responses to inquiries</li>
            <li>Protecting our rights and enforcing terms</li>
            <li>Complying with legal obligations</li>
          </ul>
        ),
      },
      {
        heading: '3. Data security',
        body: 'We apply appropriate technical and organizational measures to protect personal data from unauthorized access, use, or disclosure. No transmission method is fully secure.',
      },
      {
        heading: '4. Data sharing',
        body: (
          <>
            <p className="m-0 mb-3">We may share data:</p>
            <ul className="list-disc pl-5 m-0 space-y-1.5">
              <li>With your explicit consent</li>
              <li>When required by law</li>
              <li>With trusted service providers who help run the Platform</li>
              <li>In connection with a merger, acquisition, or asset transfer</li>
            </ul>
            <p className="m-0 mt-3">We do not sell or share personal data for third-party marketing.</p>
          </>
        ),
      },
      {
        heading: '5. Your rights',
        body: 'Depending on your jurisdiction, you may have rights to access, correct, or delete personal data we hold about you. Contact us to make a request.',
      },
      {
        heading: '6. International transfers',
        body: 'By using the Services, you understand that processing may occur in countries where DUSKPROOF or its providers operate.',
      },
      {
        heading: '7. Policy changes',
        body: 'We may update this Privacy Policy at any time. The “last updated” date on this page will change when we do.',
      },
      {
        heading: '8. Contact',
        body: (
          <>
            Privacy requests:{' '}
            <a href="mailto:office@duskproof.com" className="text-[var(--dusk-copper)] no-underline hover:text-[var(--dusk-amber)]">
              office@duskproof.com
            </a>
          </>
        ),
      },
    ],
  },
};

const LegalPage: React.FC<{ kind: LegalKind }> = ({ kind }) => {
  const doc = COPY[kind];
  const updated = 'October 30, 2026';

  useEffect(() => {
    document.title = `${doc.title} · DUSKPROOF`;
    return () => {
      document.title = 'DUSKPROOF';
    };
  }, [doc.title]);

  return (
    <PublicChrome>
      <section className="dusk-section !pt-16 md:!pt-24 !pb-20">
        <div className="dusk-container max-w-3xl">
          <BlurFade>
            <p className="text-[0.7rem] font-semibold tracking-[0.16em] uppercase text-[var(--dusk-copper)] m-0 mb-4">
              Company
            </p>
            <h1 className="dusk-page-title text-[clamp(2.25rem,5vw,3.5rem)] m-0 mb-5 leading-tight">
              {doc.title}
            </h1>
            <p className="text-[var(--dusk-muted)] text-base md:text-lg leading-relaxed m-0 mb-12 max-w-2xl">
              {doc.lead}
            </p>
          </BlurFade>

          <div className="border-t border-[var(--dusk-line)]">
            {doc.sections.map((s, i) => (
              <BlurFade key={s.heading} delay={0.03 * Math.min(i, 8)}>
                <div className="py-8 border-b border-[var(--dusk-line)]">
                  <h2 className="font-display text-xl md:text-2xl text-[var(--dusk-ink)] m-0 mb-4 font-normal">
                    {s.heading}
                  </h2>
                  <div className="text-[var(--dusk-muted)] text-[0.95rem] leading-[1.75]">{s.body}</div>
                </div>
              </BlurFade>
            ))}
          </div>

          <p className="text-xs text-[var(--dusk-faint)] mt-10 m-0">Last updated {updated}</p>
        </div>
      </section>
    </PublicChrome>
  );
};

export const TermsOfUsePage = () => <LegalPage kind="terms" />;
export const PrivacyPolicyPage = () => <LegalPage kind="privacy" />;

export default LegalPage;
